import { useRef, useState } from "react";
import { Button, Group, Modal, Stack, Text, TextInput } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCamera, IconCheck, IconPaperclip } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../../api/client";
import { DocumentCameraModal } from "../DocumentCameraModal";

type Uploaded = { name: string; ids: number[] };

/** File/camera uploader that attaches every file to ALL `shipmentIds`. */
function MultiShipmentUploader({
  shipmentIds,
  uploaded,
  onUploaded,
}: {
  shipmentIds: number[];
  uploaded: Uploaded[];
  onUploaded: (u: Uploaded) => void;
}) {
  const { t } = useTranslation();
  const [note, setNote] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  async function upload(file: File) {
    setBusy(true);
    const ids: number[] = [];
    try {
      for (const sid of shipmentIds) {
        const att = await api.uploadShipmentAttachment(sid, file, note.trim() || undefined);
        ids.push(att.id);
      }
      setNote("");
    } catch (e) {
      const tooLarge = e instanceof ApiError && (e.detail as { error?: string } | null)?.error === "file_too_large";
      notifications.show({
        color: "red",
        message: tooLarge ? t("shipments.docs.tooLarge") : e instanceof ApiError ? e.message : String(e),
      });
    } finally {
      if (ids.length > 0) onUploaded({ name: file.name, ids });
      setBusy(false);
    }
  }

  return (
    <Stack gap="sm">
      <Group gap="sm" align="end" wrap="wrap">
        <TextInput
          label={t("shipments.docs.noteLabel")}
          placeholder={t("shipments.docs.notePlaceholder")}
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
          style={{ flex: "1 1 240px" }}
        />
        <Button variant="default" leftSection={<IconPaperclip size={16} />} loading={busy}
          onClick={() => fileInputRef.current?.click()}>
          {t("shipments.docs.file")}
        </Button>
        <Button variant="default" leftSection={<IconCamera size={16} />} disabled={busy}
          onClick={() => setCameraOpen(true)}>
          {t("shipments.docs.camera")}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          hidden
          onChange={(e) => {
            const f = e.currentTarget.files?.[0];
            if (f) void upload(f);
            e.currentTarget.value = "";
          }}
        />
      </Group>
      {uploaded.length === 0 ? (
        <Text c="dimmed" size="sm">{t("shipments.docsGate.noneYet")}</Text>
      ) : (
        <Stack gap={4}>
          {uploaded.map((u, i) => (
            <Group key={i} gap="xs" wrap="nowrap">
              <IconCheck size={14} color="var(--mantine-color-green-6)" />
              <Text size="sm" truncate>{u.name}</Text>
            </Group>
          ))}
        </Stack>
      )}
      <DocumentCameraModal opened={cameraOpen} onClose={() => setCameraOpen(false)} onCapture={(f) => void upload(f)} />
    </Stack>
  );
}

/** Bulk "add document / photo" for the selected shipments (upload only). */
export function BulkDocumentsModal({
  opened,
  onClose,
  shipmentIds,
}: {
  opened: boolean;
  onClose: () => void;
  shipmentIds: number[];
}) {
  const { t } = useTranslation();
  const [uploaded, setUploaded] = useState<Uploaded[]>([]);
  function close() {
    setUploaded([]);
    onClose();
  }
  return (
    <Modal opened={opened} onClose={close} size="lg"
      title={t("shipments.docsGate.bulkTitle", { count: shipmentIds.length })}>
      <Stack>
        <MultiShipmentUploader shipmentIds={shipmentIds} uploaded={uploaded}
          onUploaded={(u) => setUploaded((p) => [...p, u])} />
        <Group justify="flex-end">
          <Button onClick={close}>{t("common.close")}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

type Pending = {
  shipmentIds: number[];
  run: () => Promise<boolean>;
  // true if the status change itself won't send the attachments to the
  // accounting group (the shipped auto-send already includes them).
  sendAfter: boolean;
  step: "ask" | "upload";
};

/**
 * Before a status change to Shipped/Delivered, ask whether there is a document
 * or photo to attach. "Yes" → upload, then change the status and send the new
 * files to the accounting group. `run` performs the status change and returns
 * whether it succeeded.
 */
export function useStatusDocsGate() {
  const { t } = useTranslation();
  const [pending, setPending] = useState<Pending | null>(null);
  const [uploaded, setUploaded] = useState<Uploaded[]>([]);
  const [busy, setBusy] = useState(false);

  function request(
    shipmentIds: number[],
    status: string,
    run: () => Promise<boolean>,
    opts: { shipAutoSends?: boolean } = {},
  ) {
    if (status !== "shipped" && status !== "delivered") {
      void run();
      return;
    }
    const sendAfter = status === "delivered" || !(opts.shipAutoSends ?? true);
    setUploaded([]);
    setPending({ shipmentIds, run, sendAfter, step: "ask" });
  }

  async function proceed() {
    if (!pending) return;
    setBusy(true);
    try {
      const ok = await pending.run();
      const attIds = uploaded.flatMap((u) => u.ids);
      if (ok && pending.sendAfter && attIds.length > 0) {
        try {
          await api.sendAttachmentsToAccounting(attIds);
          notifications.show({ color: "green", message: t("shipments.docsGate.sent") });
        } catch (e) {
          const code = e instanceof ApiError ? (e.detail as { error?: string } | null)?.error : undefined;
          notifications.show({
            color: "red",
            message: code === "not_configured" ? t("shipments.docsGate.notConfigured")
              : e instanceof ApiError ? e.message : String(e),
          });
        }
      }
      if (ok) setPending(null);
    } finally {
      setBusy(false);
    }
  }

  const element = (
    <Modal
      opened={pending !== null}
      onClose={() => !busy && setPending(null)}
      title={t("shipments.docsGate.title")}
      size={pending?.step === "upload" ? "lg" : "md"}
    >
      {pending?.step === "ask" && (
        <Stack>
          <Text size="sm">{t("shipments.docsGate.ask")}</Text>
          <Group justify="flex-end">
            <Button variant="default" loading={busy} onClick={() => void proceed()}>
              {t("shipments.docsGate.no")}
            </Button>
            <Button disabled={busy} onClick={() => setPending({ ...pending, step: "upload" })}>
              {t("shipments.docsGate.yes")}
            </Button>
          </Group>
        </Stack>
      )}
      {pending?.step === "upload" && (
        <Stack>
          <MultiShipmentUploader shipmentIds={pending.shipmentIds} uploaded={uploaded}
            onUploaded={(u) => setUploaded((p) => [...p, u])} />
          <Text size="xs" c="dimmed">{t("shipments.docsGate.uploadHint")}</Text>
          <Group justify="flex-end">
            <Button variant="default" disabled={busy} onClick={() => setPending(null)}>
              {t("common.cancel")}
            </Button>
            <Button loading={busy} disabled={uploaded.length === 0} onClick={() => void proceed()}>
              {t("shipments.docsGate.continue")}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );

  return { request, element };
}
